import React, {useEffect, useState} from "react";
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";
import {
    driverDetailsApiCall,
    wareHouseListApiCall,
} from "../../controllers/driver_repository_controller";

import {DriverListingModel} from "../../models/driver_listing_model";
import {WarehouseListModel} from "../../models/warehouse_model";
import Skeleton from "@mui/material/Skeleton";
import InfoIcon from "@mui/icons-material/Info";
import commonColorUtils from "../../../utils/common_colors";
import {parse, format} from "date-fns";
import {DriverDetailsDialog} from "./driver_details_dialog";

const DriverRepositoryBody = ({
                                  query,
                                  status,
                                  pageSize,
                                  currentPage,
                                  onCountUpdate,
                              }) => {
    let firstRender = true;

    const [isBusy, setIsBusy] = useState(false);
    const [driverDetailsList, setDriverDetailsList] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [wareHouseList, setWarehouseList] = useState([]);
    const [reportingCenter, setReportingCenter] = useState("");
    const [licenceExpireryDate, setLicenceExpireryDate] = useState(
        format(new Date(), "dd-mm-yyyy")
    );

    const columns = [
        {id: "name", label: "DRIVER", width: "25%"},
        {id: "contactDetails", label: "CONTACT DETAILS", width: "20%"},
        {id: "licenseCode", label: "LICENCE", width: "20%"},
        {id: "status", label: "STATUS", width: "20%"},
        {id: "action", label: "ACTION", width: "15%"},
    ];

    const driverDetailsListApiCall = async () => {
        try {
            setIsBusy(true);
            const response = await driverDetailsApiCall(
                query,
                status,
                pageSize,
                currentPage
            );
            if (response) {
                const driverListingModel = DriverListingModel.fromJson(response);
                setDriverDetailsList(driverListingModel.results);
                onCountUpdate(driverListingModel.count, driverListingModel.pageCount);
            }
        } catch (e) {
            console.error(e);
            setDriverDetailsList([]);
            onCountUpdate(0, 1);
        } finally {
            setIsBusy(false);
        }
    };
    const warehousesListApiCalling = async () => {
        try {
            setIsBusy(true);
            const response = await wareHouseListApiCall();
            if (response) {
                const warehouseListModel = WarehouseListModel.fromJson(response);
                setWarehouseList(warehouseListModel.warehouseList);
            }
        } catch (e) {
            console.error(e);
            setWarehouseList([]);
        } finally {
            setIsBusy(false);
        }
    };

    useEffect(() => {
        if (firstRender) {
            warehousesListApiCalling();
        }

    }, []);

    useEffect(() => {
        if (firstRender) {
            driverDetailsListApiCall();
            handleCloseDialog();
        }
        firstRender = false;

    }, [query, status, pageSize, currentPage]);



    const handleInfoClick = (driver) => {
        setSelectedDriver(driver);
        wareHouseList.forEach((element) => {
            if (element.locationId === driver.reportingCenter) {
                setReportingCenter(element.locationName);
                return;
            }
        });

        const parsedDate = parse(driver.licenseValidity, "yyyy-mm-dd", new Date());

        setLicenceExpireryDate(format(parsedDate, "dd-mm-yyyy"));

        if (wareHouseList.length !== 0) {
            setIsDialogOpen(true);
        }
    };

    const handleCloseDialog = (event, reason) => {
        ///to prevent dispose the popup on click of outside
        if (reason === "backdropClick") {
            return;
        }
        setIsDialogOpen(false);
        setSelectedDriver(null);
        setReportingCenter("");
    };

    const renderCell = (columnId, row) => {
        switch (columnId) {
            case "name":
                return (
                    <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
                        <img
                            src={
                                row?.profileImageUrl?.includes('default.png') || row?.profileImageUrl?.trim() === '' ? 'https://upload.wikimedia.org/wikipedia/commons/c/cd/Portrait_Placeholder_Square.png' : row?.profileImageUrl}
                            style={{
                                width: "50px",
                                height: "50px",
                                borderRadius: "10%",
                                objectFit: "cover",
                            }}
                        />
                        <div>{row?.name}</div>
                    </div>
                );
            case "contactDetails":
                return (
                    <div style={{display: "flex", flexDirection: "column", gap: "5px"}}>
                        <div>{row.phoneNumber ?? ""}</div>
                        <div>{row.email ?? ""}</div>
                    </div>
                );
            case "action":
                return (
                    <InfoIcon
                        onClick={() => handleInfoClick(row)}
                        style={{
                            width: "24px",
                            height: "24px",
                            cursor: "pointer",
                            color:
                                wareHouseList.length === 0
                                    ? "grey"
                                    : commonColorUtils.primaryMainBlue,
                        }}
                    />
                );
            default:
                return row[columnId];
        }
    };

    const tableCellSkeleton = (
        <Skeleton variant="rectangular" width={100} height={30}/>
    );

    return (
        <div
            style={{
                display: "flex",
                padding: "10px 20px",
                ...(!localStorage.getItem('isFromHostApplication') ? {height: "calc(90vh - 140px)",} : {height: "calc(85vh - 140px)",})
            }}
        >
            <TableContainer
                style={{
                    borderTopLeftRadius: "16px",
                    borderTopRightRadius: "16px",
                    borderRadius: "16px",
                    border: "1px solid #E0E2E6",

                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.id === "action" ? "center" : "left"}
                                    style={{
                                        width: column.width,
                                        height: "43px",
                                        backgroundColor: "#EAECEE",
                                        borderBottom: "1px solid #E5E5E5",
                                        padding: "6px 20px",
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 100,
                                        fontFamily: "Inter, Arial, sans-serif",
                                        color: "#6F727A",
                                        fontWeight: "600",
                                        fontSize: "12px",
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isBusy ? (
                            [...Array(20)].map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {columns.map((column, colIndex) => (
                                        <TableCell
                                            key={`${rowIndex}-${colIndex}`}
                                            align={column.id === "action" ? "center" : "left"}
                                        >
                                            {tableCellSkeleton}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : driverDetailsList.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    No Record found
                                </TableCell>
                            </TableRow>
                        ) : (
                            driverDetailsList.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {columns.map((column, colIndex) => (
                                        <TableCell
                                            key={`${rowIndex}-${colIndex}`}
                                            align={column.id === "action" ? "center" : "left"}
                                            style={{
                                                padding: "6px 20px",
                                                height: "38px",
                                                fontFamily: "Inter, Arial, sans-serif",
                                                borderBottom: "1px solid #E0E2E6",
                                                color:
                                                    column.id === "status"
                                                        ? row.status.toLowerCase() === "active"
                                                            ? "green"
                                                            : "red"
                                                        : "black",
                                            }}
                                        >
                                            {renderCell(column.id, row)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <DriverDetailsDialog
                isDialogOpen={isDialogOpen}
                handleCloseDialog={handleCloseDialog}
                selectedDriver={selectedDriver}
                wareHouseList={wareHouseList}
                reportingCenter={reportingCenter}
                licenceExpireryDate={licenceExpireryDate}
            />
        </div>
    );
};

export default DriverRepositoryBody;
